import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { isNativeCapacitorApp, shouldUseServiceWorker } from "./lib/runtime";
import { installDomGuard } from "./lib/domGuard";

installDomGuard();

declare global {
  interface Window {
    __bootLog?: (msg: string) => void;
  }
}

const log = (msg: string) => {
  window.__bootLog?.(msg);
};

log("main.tsx executing");

const unregisterAllServiceWorkers = async () => {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations();
    if (registrations?.length) {
      log(`Unregistering ${registrations.length} service worker(s)`);
      await Promise.all(registrations.map((r) => r.unregister()));
    }
  } catch (e) {
    log("SW cleanup error: " + String(e));
  }
};

const clearAppShellCaches = async () => {
  try {
    if (typeof caches === "undefined") return;
    const names = await caches.keys();
    // Origin-scoped: only drop this app's Workbox buckets, never messaging caches.
    const appCaches = names.filter((n) =>
      /(^|-)precache-v\d+-|(^|-)runtime-|^html-navigations$|^api-cache$/.test(n),
    );
    await Promise.allSettled(appCaches.map((n) => caches.delete(n)));
    if (appCaches.length) log(`Cleared ${appCaches.length} cache(s)`);
  } catch (e) {
    log("Cache cleanup error: " + String(e));
  }
};

// Always unregister SWs on native to prevent WKWebView caching issues
if (isNativeCapacitorApp()) {
  log("Native Capacitor detected — clearing SWs");
  void unregisterAllServiceWorkers();
} else if (shouldUseServiceWorker()) {
  log("Registering service worker");
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
    });
  });
} else {
  log("SW disabled for this context — clearing SWs and caches");
  void unregisterAllServiceWorkers().then(clearAppShellCaches);
}


log("Mounting React");
createRoot(document.getElementById("root")!).render(<App />);
log("React render called");
